// @flow
import { useCallback, useEffect, useState, type Element } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch } from 'react-redux';

import { isEnabled } from 'Config';
import useRequestIdleCallback from 'Hooks/useRequestIdleCallback';

import LoadingScreen from 'Components/LoadingScreen';

const LOADING_SCREEN_TESTID = 'pre-route-check-loading-screen';

type Props = {
  currentPath: string,
  hasPrevPath: boolean,
  onEnter?: (context: Object) => (Object) => void,
  params: Object,
  loading: boolean,
  children: Element<any>,
  prefetch: ?Array<{
    preload: Function,
  }>,
  search?: ?string,
};

const PreRouteCheck = (props: Props) => {
  const { currentPath, children, loading, onEnter, params, prefetch, search, hasPrevPath } = props;
  const dispatch = useDispatch();
  const shouldDeferRender = !!onEnter && !hasPrevPath;

  const [enterFulfilled, setEnterFulfilled] = useState(!shouldDeferRender);
  const [handledPath, setHandledPath] = useState<null | string>(null);
  const pathHandled = !!handledPath;

  const prefetchNextPageResource = useCallback(() => {
    // early fetch resource, could change to viewport detect ...etc
    prefetch?.forEach((bundle) => bundle.preload());
  }, [prefetch]);

  // execute onEnter callback on mount
  useEffect(() => {
    if (pathHandled && !enterFulfilled) {
      setEnterFulfilled(true);
    }

    if (onEnter && shouldDeferRender) {
      if (!enterFulfilled && !loading && !pathHandled) {
        setHandledPath(currentPath);

        // flushSync to avoid React's automatic batching
        // TODO: More investigation onto whether or not this
        // is necessary. Not clear if it is needed anymore
        setTimeout(() => {
          flushSync(() => {
            onEnter({ dispatch, search })({ params });
          });
        }, 0);
      }
    }
  }, [
    currentPath,
    dispatch,
    enterFulfilled,
    loading,
    onEnter,
    params,
    pathHandled,
    search,
    shouldDeferRender,
  ]);

  // prefetch resource on mount and update
  useRequestIdleCallback(prefetchNextPageResource, [prefetchNextPageResource]);

  if (!enterFulfilled || loading) {
    return isEnabled('skipLoadingScreen') ? null : (
      <LoadingScreen data-testid={LOADING_SCREEN_TESTID} />
    );
  }
  return children;
};

export default PreRouteCheck;
